import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#00695c';
const PRIMARY_MID = '#00897b';
const PRIMARY_LT  = '#e0f2f1';

const fmtQ    = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => { if (!d) return '-'; const s = String(d).split('T')[0]; const [y, m, day] = s.split('-'); return `${day}/${m}/${y}`; };
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const StockTransferInventory = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate]     = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]         = useState(fmt2Date(today));
    const [categories, setCategories] = useState([]);
    const [categoryId, setCategoryId] = useState('0');
    const [data, setData]             = useState([]);   // flat rows from API
    const [grouped, setGrouped]       = useState([]);   // grouped by VoucherNo
    const [loading, setLoading]       = useState(false);
    const [error, setError]           = useState('');
    const [fetched, setFetched]       = useState(false);
    const [search, setSearch]         = useState('');
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        if (!companyId || !token) return;
        fetchData('CategoryApi', `/${companyId}`, token)
            .then(res => setCategories(res?.response_data || res || []))
            .catch(() => setCategories([]));
    }, [companyId, token]);

    useEffect(() => {
        if (companyId && branchId && token && fromDate && toDate) {
            if (new Date(fromDate) > new Date(toDate)) {
                setData([]);
                setFetched(false);
                setError('From Date cannot be greater than To Date');
                return;
            }
            fetchReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token, categoryId]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'InventoryReportApi',
                `/StockTransferItem/${fromDate}/${toDate}/${companyId}/${branchId}/${categoryId || 0}`,
                token
            );
            const rows = res?.response_data || res || [];
            const flat = Array.isArray(rows) ? rows : [];
            setData(flat);
            // Group by VoucherNo
            const map = {};
            const order = [];
            flat.forEach(r => {
                const key = r.VoucherNo || 'N/A';
                if (!map[key]) {
                    map[key] = {
                        voucherNo:      key,
                        date:           r.StockTransferDate,
                        fromBranchName: r.FromBranchName,
                        toBranchName:   r.ToBranchName,
                        items:          [],
                        totalQty:       0,
                    };
                    order.push(key);
                }
                map[key].items.push(r);
                map[key].totalQty += parseFloat(r.Quantity) || 0;
            });
            const result = order.map(k => map[k]);
            if (result.length === 1) setExpandedId(result[0].voucherNo);
            setGrouped(result);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch stock transfer report');
        } finally {
            setLoading(false);
        }
    };

    const filteredGroups = useMemo(() => {
        if (!search.trim()) return grouped;
        const q = search.toLowerCase();
        return grouped.filter(g =>
            (g.voucherNo || '').toLowerCase().includes(q) ||
            (g.fromBranchName || '').toLowerCase().includes(q) ||
            (g.toBranchName || '').toLowerCase().includes(q) ||
            g.items.some(r => (r.ItemName || '').toLowerCase().includes(q) || (r.Code || '').toLowerCase().includes(q))
        );
    }, [grouped, search]);

    const totals = useMemo(() => ({
        vouchers: filteredGroups.length,
        qty: filteredGroups.reduce((s, g) => s + g.totalQty, 0),
    }), [filteredGroups]);

    const handleExport = async () => {
        if (!filteredGroups.length) return;
        const headers = ['Voucher No', 'Date', 'From Branch', 'To Branch', 'Code', 'Item Name', 'Qty'];
        const rows = [];
        filteredGroups.forEach(g => {
            g.items.forEach((r, idx) => {
                rows.push([
                    idx === 0 ? g.voucherNo : '',
                    idx === 0 ? fmtDate(g.date) : '',
                    idx === 0 ? g.fromBranchName : '',
                    idx === 0 ? g.toBranchName : '',
                    r.Code, r.ItemName, r.Quantity,
                ]);
            });
            rows.push(['', '', '', '', '', 'Total', fmtQ(g.totalQty)]);
            rows.push([]);
        });
        // Grand Total row
        rows.push(['', '', '', '', '', 'Grand Total', fmtQ(totals.qty)]);

        await exportStyledExcel({
            reportTitle: 'STOCK TRANSFER REPORT',
            sheetName: 'Stock Transfer',
            fileName: `StockTransfer_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            columnWidths: [14, 12, 18, 18, 10, 28, 12],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #4db6ac', color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading stock transfer..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Stock Transfer
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Inventory Report</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    {/* <SearchableDropdown
                        value={categoryId}
                        onChange={v => setCategoryId(v)}
                        options={[{ value: '0', label: 'All Categories' }, ...categories.map(c => ({ value: String(c.Id), label: c.Name }))]}
                        placeholder="All Categories"
                        minWidth={160}
                        primaryColor={PRIMARY}
                    /> */}
                    <input placeholder="Search voucher / item / branch..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 180, outline: 'none' }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filteredGroups.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary bar */}
                {fetched && filteredGroups.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Vouchers',  value: totals.vouchers,        color: PRIMARY },
                            { label: 'Total Qty', value: fmtQ(totals.qty),       color: '#0277bd' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {filteredGroups.length} vouchers
                        {search && ` (filtered from ${grouped.length})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 700, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, width: 32 }}></th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 130 }}>Voucher No</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>Date</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>From Branch</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>To Branch</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Items</th>
                                <th style={{ ...thBase, textAlign: 'right', background: '#004d40' }}>Total Qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredGroups.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : filteredGroups.map((g, i) => {
                                const isOpen = expandedId === g.voucherNo;
                                return (
                                    <>
                                        <tr key={g.voucherNo}
                                            onClick={() => setExpandedId(isOpen ? null : g.voucherNo)}
                                            style={{ background: isOpen ? PRIMARY_LT : (i % 2 === 0 ? '#fff' : '#f8fffe'), cursor: 'pointer', borderBottom: '1px solid #e2e8f0', transition: 'background 0.15s' }}>
                                            <td style={{ ...tdBase, textAlign: 'center', color: PRIMARY }}>
                                                {isOpen ? <KeyboardArrowUp sx={{ fontSize: 16 }} /> : <KeyboardArrowDown sx={{ fontSize: 16 }} />}
                                            </td>
                                            <td style={{ ...tdBase, fontWeight: 700, color: PRIMARY }}>{g.voucherNo}</td>
                                            <td style={{ ...tdBase, color: '#374151' }}>{fmtDate(g.date)}</td>
                                            <td style={{ ...tdBase, color: '#64748b' }}>{g.fromBranchName || '-'}</td>
                                            <td style={{ ...tdBase, color: '#64748b' }}>{g.toBranchName || '-'}</td>
                                            <td style={{ ...tdBase, textAlign: 'right', color: '#64748b' }}>{g.items.length}</td>
                                            <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 700 }}>{fmtQ(g.totalQty)}</td>
                                        </tr>
                                        {isOpen && (
                                            <tr key={`exp-${g.voucherNo}`}>
                                                <td colSpan={7} style={{ padding: 0, background: '#f0faf8', borderBottom: `2px solid ${PRIMARY}` }}>
                                                    <div style={{ padding: '8px 12px 8px 44px' }}>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                                            <thead>
                                                                <tr style={{ background: PRIMARY_LT }}>
                                                                    <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 700, color: PRIMARY, fontSize: 10 }}>#</th>
                                                                    <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 700, color: PRIMARY, fontSize: 10 }}>Code</th>
                                                                    <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 700, color: PRIMARY, fontSize: 10 }}>Item Name</th>
                                                                    <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: PRIMARY, fontSize: 10 }}>Qty</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {g.items.map((r, idx) => (
                                                                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #e2e8f0' }}>
                                                                        <td style={{ padding: '4px 8px', color: '#94a3b8' }}>{idx + 1}</td>
                                                                        <td style={{ padding: '4px 8px', color: '#64748b', fontWeight: 600 }}>{r.Code || '-'}</td>
                                                                        <td style={{ padding: '4px 8px', fontWeight: 600, color: PRIMARY }}>{r.ItemName || '-'}</td>
                                                                        <td style={{ padding: '4px 8px', textAlign: 'right', color: '#0277bd', fontWeight: 600 }}>{fmtQ(r.Quantity)}</td>
                                                                    </tr>
                                                                ))}
                                                                <tr style={{ background: PRIMARY_LT }}>
                                                                    <td colSpan={3} style={{ padding: '5px 8px', fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Total</td>
                                                                    <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtQ(g.totalQty)}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                );
                            })}
                        </tbody>
                        {filteredGroups.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700, position: 'sticky', bottom: 0 }}>
                                    <td colSpan={5} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Grand Total</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#64748b', fontWeight: 700 }}>{data.length}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 700 }}>{fmtQ(totals.qty)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StockTransferInventory;
